import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown, Modal, Tab, Nav } from 'react-bootstrap';
import { getAllEvents, deleteEvent, cloneEvent } from '../../lib/api/events';
import { getSurveysForUser, type SurveyRecordWithEvent } from '../../lib/api/surveys';
import {
  getAllUsers,
  getUserDetails,
  promoteToAdmin,
  demoteFromAdmin,
  getUserApplications,
  type UserListItem,
  type UserDetails
} from '../../lib/api/users';
import type { Event } from '../../types/database';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { captureError } from '../../lib/capture-error';
import { formatLocalDate, formatLocalDateRange } from '../../lib/dates';
import './AdminDashboardPage.css';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  // Tab state from URL
  const activeTab = searchParams.get('tab') || 'events';

  // Events state
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [cloning, setCloning] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // User detail modal state
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [selectedUserApplications, setSelectedUserApplications] = useState<any[]>([]);
  const [selectedUserSurveys, setSelectedUserSurveys] = useState<SurveyRecordWithEvent[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Promote/demote state
  const [promoting, setPromoting] = useState<string | null>(null);
  const [demoteConfirm, setDemoteConfirm] = useState<string | null>(null);

  // Derived user lists
  const adminUsers = users.filter(u => u.is_admin);
  const regularUsers = users.filter(u => !u.is_admin);

  // Filter by search
  const filteredAdmins = searchTerm
    ? adminUsers.filter(u =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : adminUsers;

  const filteredRegularUsers = searchTerm
    ? regularUsers.filter(u =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : regularUsers;

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'events') {
      loadEvents();
    } else if (activeTab === 'users') {
      loadUsers();
    }
  }, [activeTab]);

  // Events functions
  async function loadEvents() {
    try {
      setEventsLoading(true);
      const data = await getAllEvents();
      setEvents(data);
    } catch (err) {
        setEventsError('Failed to load events');
        captureError(err);
      } finally {
      setEventsLoading(false);
    }
  }

  async function handleDeleteEvent(id: string) {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }

    try {
      await deleteEvent(id);
      setEvents(events.filter((e) => e.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
        showToast('Failed to delete event', 'error');
        captureError(err);
      }
  }

  async function handleCloneEvent(eventId: string) {
    try {
      setCloning(eventId);
      const newEvent = await cloneEvent(eventId);
      navigate(`/admin/events/${newEvent.id}`);
    } catch (err) {
      showToast('Failed to clone event', 'error');
      captureError(err);
    } finally {
      setCloning(null);
    }
  }

  function getStatusBadge(status: string) {
    const statusClass = {
      draft: 'bg-secondary',
      published: 'bg-success',
      archived: 'bg-warning',
    }[status] || 'bg-secondary';

    return <span className={`badge ${statusClass}`}>{status}</span>;
  }

  // Users functions
  async function loadUsers() {
    try {
      setUsersLoading(true);
      const data = await getAllUsers();
      setUsers(data);
    } catch (err) {
      setUsersError('Failed to load users');
      captureError(err);
    } finally {
      setUsersLoading(false);
    }
  }

  async function handleViewDetails(userId: string) {
    setLoadingDetails(true);
    setShowDetailModal(true);
    try {
      const [details, applications] = await Promise.all([
        getUserDetails(userId),
        getUserApplications(userId)
      ]);
      const surveys = await getSurveysForUser(userId);
      setSelectedUser(details);
      setSelectedUserApplications(applications);
      setSelectedUserSurveys(surveys);
    } catch (err) {
      captureError(err, 'Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  }

  async function handlePromote(user: UserListItem) {
    setPromoting(user.id);
    try {
      await promoteToAdmin(user.id, 'reviewer', user.full_name || undefined, user.email);
      await loadUsers();
    } catch (err: any) {
      showToast(err.message || 'Failed to make admin', 'error');
      captureError(err);
    } finally {
      setPromoting(null);
    }
  }

  async function handleDemote(userId: string) {
    if (demoteConfirm !== userId) {
      setDemoteConfirm(userId);
      return;
    }

    if (userId === currentUser?.id) {
      showToast("You cannot remove your own admin privileges", 'error');
      setDemoteConfirm(null);
      return;
    }

    try {
      await demoteFromAdmin(userId);
      await loadUsers();
      setDemoteConfirm(null);
    } catch (err) {
      showToast('Failed to remove admin', 'error');
      captureError(err);
    }
  }

  function handleTabSelect(key: string | null) {
    if (key) {
      setSearchParams({ tab: key });
    }
  }

  function renderUserTypeBadges(user: UserListItem) {
    if (user.has_profile) {
      return (
        <div className="d-flex flex-wrap gap-1">
          <span className="badge bg-primary">Attendee</span>
          {user.profile_completed ? (
            <span className="badge bg-success">Complete</span>
          ) : (
            <span className="badge bg-warning text-dark">Incomplete</span>
          )}
        </div>
      );
    }
    return <span className="badge bg-secondary">Registered</span>;
  }

  // Render a user row (used in both sections)
  function renderUserRow(user: UserListItem, isAdminSection: boolean) {
    return (
      <tr key={user.id}>
        <td>
          <div className="d-flex align-items-center">
            <div className="user-avatar me-3">
              <i className={`fas ${isAdminSection ? 'fa-user-shield' : 'fa-user'} fa-lg ${isAdminSection ? 'text-primary' : 'text-secondary'}`}></i>
            </div>
            <div>
              <strong>{user.full_name || 'No name'}</strong>
              <br />
              <small className="text-muted">{user.email}</small>
            </div>
          </div>
        </td>
        <td>{renderUserTypeBadges(user)}</td>
        <td>
          <span className="badge bg-info">{user.application_count}</span>
        </td>
        <td>
          <small>{new Date(user.created_at).toLocaleDateString()}</small>
        </td>
        <td className="text-end">
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              id={`user-actions-${user.id}`}
            >
              <i className="fas fa-ellipsis-v"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
              <Dropdown.Item onClick={() => handleViewDetails(user.id)}>
                <i className="fas fa-eye me-2"></i>
                View Details
              </Dropdown.Item>
              <Dropdown.Divider />
              {isAdminSection ? (
                <Dropdown.Item
                  onClick={() => handleDemote(user.id)}
                  className={demoteConfirm === user.id ? 'text-danger' : ''}
                  disabled={user.id === currentUser?.id}
                >
                  <i className="fas fa-user-minus me-2"></i>
                  {demoteConfirm === user.id ? 'Click again to confirm' : 'Remove Admin'}
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  onClick={() => handlePromote(user)}
                  disabled={promoting === user.id}
                >
                  {promoting === user.id ? (
                    'Making admin...'
                  ) : (
                    <>
                      <i className="fas fa-user-shield me-2"></i>
                      Make Admin
                    </>
                  )}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      </tr>
    );
  }

  return (
    <div className="container my-5">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h1 className="mb-0">Admin Dashboard</h1>
        <Link to="/admin/churches" className="btn btn-outline-secondary">
          <i className="fas fa-church me-2" aria-hidden></i>
          Church Names
        </Link>
      </div>

      <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="events">
              <i className="fas fa-calendar-alt me-2"></i>
              Events
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="users">
              <i className="fas fa-users me-2"></i>
              Users
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Events Tab */}
          <Tab.Pane eventKey="events">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="h4 mb-0">Manage Events</h2>
              <Link to="/admin/events/new" className="btn btn-primary">
                <i className="fas fa-plus me-2"></i>
                Create New Event
              </Link>
            </div>

            {eventsError && (
              <div className="alert alert-danger" role="alert">
                {eventsError}
              </div>
            )}

            {eventsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : events.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-calendar-plus fa-3x text-muted mb-3"></i>
                  <h4>No Events Yet</h4>
                  <p className="text-muted mb-4">
                    Create your first event to get started
                  </p>
                  <Link to="/admin/events/new" className="btn btn-primary">
                    <i className="fas fa-plus me-2" aria-hidden></i>
                    Create Event
                  </Link>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Location</th>
                        <th>Dates</th>
                        <th>Status</th>
                        <th>Registration Deadline</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event) => (
                        <tr key={event.id}>
                          <td>
                            <strong>{event.title}</strong>
                            <br />
                            <small className="text-muted">/{event.slug}</small>
                          </td>
                          <td>
                            {event.location_city && event.location_country
                              ? `${event.location_city}, ${event.location_country}`
                              : '—'}
                          </td>
                          <td>
                            <small>
                              {formatLocalDateRange(event.start_date, event.end_date)}
                            </small>
                          </td>
                          <td>{getStatusBadge(event.status)}</td>
                          <td>
                            <small>
                              {formatLocalDate(event.registration_deadline)}
                            </small>
                          </td>
                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="outline-secondary"
                                size="sm"
                                id={`event-actions-${event.id}`}
                              >
                                <i className="fas fa-ellipsis-v"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                                <Dropdown.Item as={Link} to={`/admin/events/${event.id}`}>
                                  <i className="fas fa-edit me-2"></i>
                                  Edit Event
                                </Dropdown.Item>
                                <Dropdown.Item as={Link} to={`/admin/events/${event.id}/applications`}>
                                  <i className="fas fa-file-alt me-2"></i>
                                  View Applications
                                </Dropdown.Item>
                                <Dropdown.Item as={Link} to={`/admin/events/${event.id}/groups`}>
                                  <i className="fas fa-users me-2"></i>
                                  Field Groups
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={() => handleCloneEvent(event.id)}
                                  disabled={cloning === event.id}
                                >
                                  {cloning === event.id ? (
                                    'Cloning...'
                                  ) : (
                                    <>
                                      <i className="fas fa-copy me-2"></i>
                                      Clone Event
                                    </>
                                  )}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={() => handleDeleteEvent(event.id)}
                                  className={deleteConfirm === event.id ? 'text-danger' : ''}
                                >
                                  <i className="fas fa-trash me-2"></i>
                                  {deleteConfirm === event.id ? 'Click again to confirm' : 'Delete Event'}
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Tab.Pane>

          {/* Users Tab */}
          <Tab.Pane eventKey="users">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="h4 mb-0">Manage Users</h2>
              <div className="d-flex gap-2">
                <span className="badge bg-primary fs-6">
                  {adminUsers.length} admins
                </span>
                <span className="badge bg-secondary fs-6">
                  {regularUsers.length} users
                </span>
              </div>
            </div>

            {usersError && (
              <div className="alert alert-danger" role="alert">
                {usersError}
              </div>
            )}

            {/* Search */}
            <div className="mb-4">
              <div className="input-group" style={{ maxWidth: '400px' }}>
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {usersLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Administrators Section */}
                <div className="card mb-4">
                  <div className="card-header admin-section-header">
                    <i className="fas fa-user-shield me-2"></i>
                    Administrators ({filteredAdmins.length})
                  </div>
                  {filteredAdmins.length === 0 ? (
                    <div className="card-body text-center py-4">
                      <p className="text-muted mb-0">
                        {searchTerm ? 'No admins match your search' : 'No administrators'}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Type</th>
                            <th>Applications</th>
                            <th>Joined</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAdmins.map(user => renderUserRow(user, true))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Regular Users Section */}
                <div className="card">
                  <div className="card-header user-section-header">
                    <i className="fas fa-users me-2"></i>
                    Regular Users ({filteredRegularUsers.length})
                  </div>
                  {filteredRegularUsers.length === 0 ? (
                    <div className="card-body text-center py-4">
                      <p className="text-muted mb-0">
                        {searchTerm ? 'No users match your search' : 'No regular users'}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Type</th>
                            <th>Applications</th>
                            <th>Joined</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRegularUsers.map(user => renderUserRow(user, false))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* User Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : selectedUser ? (
            <div>
              {/* Basic Info */}
              <div className="mb-4">
                <h5 className="border-bottom pb-2">Basic Information</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Name:</strong> {selectedUser.full_name || 'Not provided'}</p>
                    <p><strong>Email:</strong> {selectedUser.email}</p>
                    <p><strong>Joined:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="col-md-6">
                    <p>
                      <strong>Status:</strong>{' '}
                      {selectedUser.is_admin ? (
                        <span className="badge bg-primary">Admin</span>
                      ) : (
                        <span className="badge bg-secondary">User</span>
                      )}
                    </p>
                    <p className="d-flex align-items-center gap-2 flex-wrap">
                      <strong>Type:</strong>
                      {renderUserTypeBadges(selectedUser)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Details */}
              {selectedUser.profile && (
                <div className="mb-4">
                  <h5 className="border-bottom pb-2">Profile Details</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Phone:</strong> {selectedUser.profile.cell_phone || 'Not provided'}</p>
                      <p><strong>Date of Birth:</strong> {selectedUser.profile.date_of_birth ? formatLocalDate(selectedUser.profile.date_of_birth) : 'Not provided'}</p>
                      <p><strong>Gender:</strong> {selectedUser.profile.gender || 'Not provided'}</p>
                    </div>
                    <div className="col-md-6">
                      <p>
                        <strong>Address:</strong><br />
                        {selectedUser.profile.address_line1 || 'Not provided'}
                        {selectedUser.profile.address_line2 && <><br />{selectedUser.profile.address_line2}</>}
                        {selectedUser.profile.city && <><br />{selectedUser.profile.city}, {selectedUser.profile.state} {selectedUser.profile.zip}</>}
                      </p>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  {selectedUser.profile.emergency_contact && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Emergency Contact</h6>
                      <p className="mb-1">
                        {selectedUser.profile.emergency_contact.first_name} {selectedUser.profile.emergency_contact.last_name}
                      </p>
                      <p className="mb-0 text-muted">{selectedUser.profile.emergency_contact.phone_number}</p>
                    </div>
                  )}

                  {/* Church Info */}
                  {selectedUser.profile.church_info && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6>Church Information</h6>
                      <p className="mb-1">{selectedUser.profile.church_info.church_name || 'Church name not provided'}</p>
                      <p className="mb-0 text-muted">
                        Pastor: {selectedUser.profile.church_info.pastor_first_name} {selectedUser.profile.church_info.pastor_last_name}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Applications */}
              <div className="mb-4">
                <h5 className="border-bottom pb-2">Applications ({selectedUserApplications.length})</h5>
                {selectedUserApplications.length === 0 ? (
                  <p className="text-muted">No applications</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Status</th>
                          <th>Submitted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUserApplications.map((app: any) => (
                          <tr key={app.id}>
                            <td>{app.events?.title || 'Unknown Event'}</td>
                            <td>
                              <span className={`badge bg-${
                                app.status === 'approved' ? 'success' :
                                app.status === 'rejected' ? 'danger' :
                                app.status === 'submitted' ? 'primary' :
                                'secondary'
                              }`}>
                                {app.status}
                              </span>
                            </td>
                            <td>
                              {app.submitted_at
                                ? new Date(app.submitted_at).toLocaleDateString()
                                : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="mb-2">
                <h5 className="border-bottom pb-2">Event Surveys ({selectedUserSurveys.length})</h5>
                {selectedUserSurveys.length === 0 ? (
                  <p className="text-muted">No surveys sent.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th>Survey</th>
                          <th>Sent</th>
                          <th>Submitted</th>
                          <th>Response</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUserSurveys.map((survey) => (
                          <tr key={survey.id}>
                            <td>{survey.event_application?.event?.title || 'Unknown Event'}</td>
                            <td>{survey.survey_key === 'pre_event' ? 'Pre-Event' : 'Post-Event'}</td>
                            <td>{survey.sent_at ? new Date(survey.sent_at).toLocaleDateString() : '—'}</td>
                            <td>{survey.submitted_at ? new Date(survey.submitted_at).toLocaleDateString() : '—'}</td>
                            <td>
                              {survey.submitted_at ? (
                                <pre className="mb-0 small bg-light p-2 rounded" style={{ maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(survey.response_payload || {}, null, 2)}
                                </pre>
                              ) : (
                                <span className="text-muted">Pending</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-muted">User not found</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
            <i className="fas fa-times me-2" aria-hidden></i>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminDashboardPage;
